import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ImagePlus, X } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BeatMetaFields, appendMeta, EMPTY_META } from "@/components/BeatMetaFields";
import { coverUrl } from "@/context/PlayerContext";

export const EditBeatDialog = ({ beat, genres, onClose, onSaved }) => {
  const [title, setTitle] = useState("");
  const [meta, setMeta] = useState(EMPTY_META);
  const [cover, setCover] = useState(null);
  const [removeCover, setRemoveCover] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!beat) return;
    setTitle(beat.title);
    setMeta({ bpm: beat.bpm ?? "", key: beat.key ?? "", genre: beat.genre ?? "", for_sale: beat.for_sale !== false });
    setCover(null);
    setRemoveCover(false);
  }, [beat]);

  const preview = cover ? URL.createObjectURL(cover) : !removeCover ? coverUrl(beat) : null;

  const save = async () => {
    const fd = new FormData();
    fd.append("title", title);
    appendMeta(fd, meta);
    if (cover) fd.append("cover", cover);
    else if (removeCover) fd.append("remove_cover", "true");
    setSaving(true);
    try {
      const { data } = await api.patch(`/beats/${beat.beat_id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Beat updated");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!beat} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F0F0F] border-white/10 text-white max-w-lg" data-testid="edit-beat-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Edit beat</DialogTitle></DialogHeader>
        <div className="flex gap-4 items-start">
          <label className="relative h-24 w-24 shrink-0 border border-dashed border-white/20 hover:border-[#D90429] cursor-pointer flex items-center justify-center overflow-hidden" data-testid="edit-beat-cover-picker">
            {preview ? <img src={preview} alt="cover preview" className="h-full w-full object-cover" /> : <ImagePlus className="h-6 w-6 text-white/40" />}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="edit-beat-cover-input"
              onChange={(e) => { setCover(e.target.files[0] || null); setRemoveCover(false); }} />
          </label>
          <div className="flex-1 space-y-2">
            <label className="text-xs text-[#A0A0A0] uppercase tracking-wider">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="edit-beat-title-input"
              className="w-full bg-[#121212] border border-white/10 focus:border-[#D90429] outline-none px-3 py-2.5 text-sm" />
            {preview && (
              <button onClick={() => { setCover(null); setRemoveCover(true); }} data-testid="edit-beat-remove-cover"
                className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-[#D90429]"><X className="h-3 w-3" /> Remove cover</button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3"><BeatMetaFields value={meta} onChange={setMeta} genres={genres} prefix="edit-beat" /></div>
        <button onClick={save} disabled={saving} data-testid="edit-beat-save-button"
          className="mt-2 bg-[#D90429] hover:bg-[#ED2939] text-white px-6 py-3 font-bold uppercase tracking-wide text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
        </button>
      </DialogContent>
    </Dialog>
  );
};
